import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useUpdateBuyListStatus } from '~/api/buyList';
import { ConfirmDialog } from '~/components/overlays/ConfirmDialog';
import { Button, type IButtonProps } from '~/components/ui';
import { BuyListStatus } from '~/graphql/graphql';
import { type ClientError, extractGraphQLErrorCodes } from '~/utils';

const OPEN_BUY_LIST_EXISTS_CODE = 'open-buy-list-exists';

type StatusChange = {
  target: BuyListStatus;
  actionKey: string;
  titleKey: string;
  messageKey: string;
  testId: string;
  confirmVariant: IButtonProps['variant'];
};

const MARK_FULFILLED: StatusChange = {
  target: BuyListStatus.Fulfilled,
  actionKey: 'inventory.buy_list.status_change.mark_fulfilled',
  titleKey: 'inventory.buy_list.status_change.fulfil_title',
  messageKey: 'inventory.buy_list.status_change.fulfil_message',
  testId: 'buy-list-mark-fulfilled',
  confirmVariant: 'primary',
};

const CLOSE: StatusChange = {
  target: BuyListStatus.Closed,
  actionKey: 'inventory.buy_list.status_change.close',
  titleKey: 'inventory.buy_list.status_change.close_title',
  messageKey: 'inventory.buy_list.status_change.close_message',
  testId: 'buy-list-close',
  confirmVariant: 'destructive',
};

const REOPEN: StatusChange = {
  target: BuyListStatus.Open,
  actionKey: 'inventory.buy_list.status_change.reopen',
  titleKey: 'inventory.buy_list.status_change.reopen_title',
  messageKey: 'inventory.buy_list.status_change.reopen_message',
  testId: 'buy-list-reopen',
  confirmVariant: 'primary',
};

function changesFrom(status: BuyListStatus): StatusChange[] {
  return status === BuyListStatus.Open ? [MARK_FULFILLED, CLOSE] : [REOPEN];
}

type BuyListStatusActionsProps = {
  readonly buyListId: string;
  readonly status: BuyListStatus;
  readonly openRequestsForQuote: number;
  readonly areAllRequestsForQuoteLoaded: boolean;
};

export function BuyListStatusActions({
  buyListId,
  status,
  openRequestsForQuote,
  areAllRequestsForQuoteLoaded,
}: BuyListStatusActionsProps) {
  const { t } = useTranslation('inventory');
  const { mutate: updateBuyListStatus, isPending } = useUpdateBuyListStatus();
  const [pendingChange, setPendingChange] = useState<StatusChange | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const closeDialog = () => {
    setPendingChange(null);
    setErrorMessage(null);
  };

  const errorMessageFor = (error: unknown) =>
    extractGraphQLErrorCodes(error as ClientError).includes(
      OPEN_BUY_LIST_EXISTS_CODE
    )
      ? t('inventory.buy_list.status_change.reopen_conflict')
      : t('inventory.buy_list.status_change.change_failed');

  const confirmChange = (change: StatusChange) => {
    setErrorMessage(null);
    updateBuyListStatus(
      { buyListId, status: change.target },
      {
        onSuccess: () => {
          toast.success(t('inventory.buy_list.status_change.changed'));
          closeDialog();
        },
        onError: (error) => setErrorMessage(errorMessageFor(error)),
      }
    );
  };

  const openRequestsWarningFor = (change: StatusChange) => {
    if (change.target === BuyListStatus.Open) return null;
    if (!areAllRequestsForQuoteLoaded)
      return t(
        'inventory.buy_list.status_change.some_requests_for_quote_may_stay_open'
      );
    if (openRequestsForQuote > 0)
      return t('inventory.buy_list.status_change.open_requests_for_quote', {
        count: openRequestsForQuote,
      });
    return null;
  };
  const openRequestsWarning =
    pendingChange && openRequestsWarningFor(pendingChange);

  return (
    <>
      {changesFrom(status).map((change) => (
        <Button
          key={change.testId}
          variant="secondary"
          width="auto"
          data-testid={change.testId}
          onClick={() => setPendingChange(change)}
        >
          {t(change.actionKey)}
        </Button>
      ))}
      {pendingChange && (
        <ConfirmDialog
          isOpen
          title={t(pendingChange.titleKey)}
          message={
            <>
              <span className="block">{t(pendingChange.messageKey)}</span>
              {openRequestsWarning && (
                <span
                  className="block mt-2"
                  data-testid="buy-list-status-open-requests"
                >
                  {openRequestsWarning}
                </span>
              )}
              {errorMessage && (
                <span
                  role="alert"
                  className="block mt-2 text-error-600"
                  data-testid="buy-list-status-error"
                >
                  {errorMessage}
                </span>
              )}
            </>
          }
          cancelLabel={t('inventory.buy_list.status_change.cancel')}
          confirmLabel={t(pendingChange.actionKey)}
          confirmVariant={pendingChange.confirmVariant}
          isPending={isPending}
          onCancel={closeDialog}
          onConfirm={() => confirmChange(pendingChange)}
        />
      )}
    </>
  );
}
